import { useState } from "react";
import { Resource, ResourceType } from "@/types/resource";
import FilterSection from "@/components/FilterSection";
import ResourceList from "@/components/ResourceList";
import ResourceSubmissionForm from "@/components/ResourceSubmissionForm";
import AdminReview from "@/components/AdminReview";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams } from "react-router-dom";
import { Toggle } from "@/components/ui/toggle";
import { Database } from "@/integrations/supabase/types";

type Mode = "receive" | "donate";
type DonationResourceType = Database['public']['Enums']['donation_resource_type'];

const Index = () => {
  const [mode, setMode] = useState<Mode>("receive");
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get('admin') === 'true';
  const [selectedReceiveTypes, setSelectedReceiveTypes] = useState<ResourceType[]>([]);
  const [selectedDonateTypes, setSelectedDonateTypes] = useState<DonationResourceType[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: resources = [], isLoading } = useQuery({
    queryKey: ['resources', mode],
    queryFn: async () => {
      console.log('Fetching resources for mode:', mode);
      
      const tableName = mode === "receive" ? 'resources' : 'donation_resources';
      const tagsJoinTable = mode === "receive" ? 'resource_tags' : 'donation_resource_tags';
      
      const { data, error } = await supabase
        .from(tableName)
        .select(`
          *,
          ${tagsJoinTable} (
            tags (
              *
            )
          )
        `);
      
      if (error) {
        console.error('Error fetching resources:', error);
        throw error;
      }

      console.log('Raw data from Supabase:', data);
      
      // Transform the data to match the Resource type, avoiding circular references
      const transformedData = data.map((resource: any) => {
        // Extract tags directly from the join table array
        const tags = resource[tagsJoinTable]?.map((tagJoin: any) => ({
          id: tagJoin.tags.id,
          name: tagJoin.tags.name
        })) || [];

        return {
          ...resource,
          tags
        };
      });

      console.log('Transformed data:', transformedData);
      
      return transformedData;
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false
  });

  const selectedTypes = mode === "receive" ? selectedReceiveTypes : selectedDonateTypes;
  const setSelectedTypes = (types: ResourceType[] | DonationResourceType[]) => {
    if (mode === "receive") {
      setSelectedReceiveTypes(types as ResourceType[]);
    } else {
      setSelectedDonateTypes(types as DonationResourceType[]);
    }
  };

  const filteredResources = resources.filter((resource) => {
    // Check if resource matches type filter
    const resourceType = mode === "receive" 
      ? (resource.type as ResourceType)
      : (resource.type as DonationResourceType);
    
    // Check if resource matches selected types or has matching tags
    const matchesType = selectedTypes.length === 0 || 
      selectedTypes.includes(resourceType as never) ||
      (resource.tags && resource.tags.some(tag => 
        selectedTypes.includes(tag.name.toLowerCase() as never)
      ));

    // Check if resource matches search query in name, description, or tags
    const matchesSearch = searchQuery === "" ||
      resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (resource.tags && resource.tags.some(tag => 
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
      ));

    return matchesType && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">LA Wildfire Resource Center</h1>
              <p className="mt-2 text-gray-600">Find emergency resources and support services</p>
              
              {/* Mobile Toggle and Submit Button */}
              <div className="flex items-center justify-between mt-4 md:hidden">
                <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
                  <Toggle
                    pressed={mode === "receive"}
                    onPressedChange={() => setMode("receive")}
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  >
                    Receive Help
                  </Toggle>
                  <Toggle
                    pressed={mode === "donate"}
                    onPressedChange={() => setMode("donate")}
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  >
                    Donate
                  </Toggle>
                </div>
                <ResourceSubmissionForm mode={mode} />
              </div>
            </div>
            
            {/* Desktop Controls */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
                <Toggle
                  pressed={mode === "receive"}
                  onPressedChange={() => setMode("receive")}
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  Receive Help
                </Toggle>
                <Toggle
                  pressed={mode === "donate"}
                  onPressedChange={() => setMode("donate")}
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  Donate
                </Toggle>
              </div>
              {isAdmin && <AdminReview />}
              <ResourceSubmissionForm mode={mode} />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <FilterSection
          selectedTypes={selectedTypes}
          setSelectedTypes={setSelectedTypes}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          mode={mode}
        />

        {isLoading ? (
          <div className="text-center py-8">Loading resources...</div>
        ) : (
          <ResourceList resources={filteredResources} />
        )}
      </main>
    </div>
  );
};

export default Index;